import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { FontLoader } from "@/components/FontLoader";

import { useFullscreen } from "@/hooks/useFullscreen";
import { FullscreenButton } from "@/components/FullscreenButton";
import { useBackgroundColorApplicator } from "@/hooks/useBackgroundColorApplicator";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import { AuthProvider } from "./context/AuthContext";
import { AppSettingsProvider } from "./context/app-settings";
import { AccessibilityProvider } from "./context/AccessibilityContext";

import { useEffect, useState } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Index from "./pages/Index";
import Tours from "./pages/Tours";
import TourDetail from "./pages/TourDetail";
import TourMap from "./pages/TourMap";
import ExhibitAccess from "./pages/ExhibitAccess";
import Exhibit from "./pages/Exhibit";
import Favorites from "./pages/Favorites";
import VisitorInfo from "./pages/VisitorInfo";
import NotFound from "./pages/NotFound";
import { authRoutes } from "./routes/authRoutes";
import { cmsRoutes } from './routes/cmsRoutes';
import { debugLogger } from "./utils/debugLogger";
import { initializeSecurityMeasures } from "./utils/security";

// Create and configure the query client with settings that prevent automatic redirects
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // CRITICAL: Prevent all automatic refetching that could trigger navigation
      staleTime: 60 * 60 * 1000, // 1 hour - very long stale time
      retry: false, // NO retries to prevent redirect loops
      refetchOnWindowFocus: false, // CRITICAL: Don't refetch when tab becomes active
      refetchOnReconnect: false, // Don't refetch on network reconnect
      refetchOnMount: false, // Don't refetch when component mounts
      refetchInterval: false, // No background polling
      refetchIntervalInBackground: false // No background polling when tab inactive
    },
  },
});

// Scroll to top component - completely stable
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    debugLogger.log('SCROLL', `Path changed to: ${pathname}`);
    // Simple scroll without any conditions that could cause issues
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AppContent() {
  const { markUserInteraction } = useFullscreen();
  
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppSettingsProvider>
          <AppWithAuth />
        </AppSettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppWithAuth() {
  const { markUserInteraction } = useFullscreen();
  
  return (
    <LanguageProvider>
      <FavoritesProvider>
        <AccessibilityProvider>
          <TooltipProvider>
            <AppWithBackground />
          </TooltipProvider>
        </AccessibilityProvider>
      </FavoritesProvider>
    </LanguageProvider>
  );
}

function AppWithBackground() {
  const { markUserInteraction } = useFullscreen();
  
  // Apply background color globally based on app settings
  useBackgroundColorApplicator();
  
  return (
    <>
      <FontLoader />
      <Toaster />
      <Sonner />
      <div className="flex flex-col min-h-screen" onClick={markUserInteraction}>
        <FullscreenButton />
        <Header />
        <ScrollToTop />
        <main className="flex-grow pb-16">
          <Routes>
            {/* CMS routes - Ensure they render correctly */}
            {cmsRoutes}
            
            {/* Public routes */}
            <Route path="/" element={<Index />} />
            <Route path="/tours" element={<Tours />} />
            <Route path="/tours/:id" element={<TourDetail />} />
            <Route path="/tour-map/:id" element={<TourMap />} />
            <Route path="/exhibit-access" element={<ExhibitAccess />} />
            <Route path="/exhibit/:id" element={<Exhibit />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/visitor-info" element={<VisitorInfo />} />
            
            {/* Auth routes */}
            {authRoutes}
            
            {/* 404 route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </>
  );
}

const App = () => {
  debugLogger.log('APP', `App component rendering at ${new Date().toISOString()}`);
  
  // Initialize security measures once
  useEffect(() => {
    initializeSecurityMeasures();
  }, []);
  
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
};

export default App;
